import React, { useState } from 'react';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { DateSelect } from '@components/UI-Elements/Date/DateSelect';
import ButtonDesigned from '@UI-Elements/Buttons/ButtonDesigned';
import { InputFieldWithTitles } from '@UI-Elements/Select/InputFieldWithTitles';
import { OptionSelect } from '@UI-Elements/Select/OptionSelect';
import { IFutureOrder } from '@sp-types/FutureOrderType';
import { Product } from '@sp-types/productType';
import { futureOrderProductDistributionTypes } from '@utils/MonthYear/distributeValueThroughDates/distributeValueThroughDates';
import { MonthYear } from '@utils/MonthYear/monthYear';
import { isAdjustmentMethodKey } from '../../RawMaterialsExposure/RawMaterialsUtils';
import { DeliveryDatePicker } from '../ExpectedDeliveries/DeliveryDatesPick/FutureOrderDeliveryDatePicker';
import { updateFutureOrderValueCalendars } from '../../AddNewFutureOrderUtils';
import { Timestamp } from 'firebase/firestore';
import { useCurrencyRates } from '../../../../../hooks/useCurrencyRates';
import { formatToPriceInDollars } from '../../../../../utils/FormatValue';

interface OrderDetailsInputFieldsProps {
  futureOrder: IFutureOrder;
  setFutureOrder: (_value: IFutureOrder) => void;
  products: Product[];
  isEditable?: boolean;
}
export const OrderDetailsInputFields: React.FC<OrderDetailsInputFieldsProps> = ({
  futureOrder,
  setFutureOrder,
  products,
  isEditable = true,
}) => {
  const [dirtyFutureOrder, setDirtyFutureOrder] = useState<IFutureOrder>(futureOrder);

  const { pricesMap, currencies, getCurrencySymbol, getCurrencyInUSD } = useCurrencyRates();
  const { orderDetails, orderId, productId, clientName } = dirtyFutureOrder;
  const { deliveryDates, deliveryType, quantity, pricePerUnit } = orderDetails;
  // const [isDialogOpen, setDialogOpen] = useState(false);
  const { materialsMap } = useMaterialsStore();

  // const handleApplyClick = () => {
  //   setDialogOpen(true);
  // };

  React.useEffect(() => {
    if (futureOrder?.uuid) {
      setDirtyFutureOrder({ ...futureOrder });
    }
  }, [futureOrder?.uuid]);

  const cleanProductCalendar = ({ orderDetails, ...futureOrderProps }: IFutureOrder): IFutureOrder => ({
    ...futureOrderProps,
    orderDetails: { ...orderDetails, productDeliveryCalendar: undefined },
  });

  const handleApplyClick = () => {
    //setDialogOpen(false);
    const dirtyFOSupplierExposure = dirtyFutureOrder.supplierExposure.length
      ? dirtyFutureOrder.supplierExposure[0]
      : undefined;
    const adjustment = dirtyFOSupplierExposure
      ? isAdjustmentMethodKey(dirtyFOSupplierExposure.adjustment)
        ? dirtyFOSupplierExposure?.adjustment
        : 'BackToBack'
      : 'BackToBack';
    const leadTime = dirtyFOSupplierExposure?.supplyLeadTime || 0;

    const { productDeliveryCalendar } = updateFutureOrderValueCalendars(
      cleanProductCalendar(dirtyFutureOrder),
      products,
      materialsMap,
      adjustment,
      leadTime,
    );

    setFutureOrder({
      ...dirtyFutureOrder,
      supplierExposure: [],
      clientExposure: {},
      orderDetails: { ...orderDetails, productDeliveryCalendar },
    });
  };
  const isFormValid = (): boolean =>
    !!(productId && orderId && deliveryType && deliveryDates && pricePerUnit && quantity && clientName && productId);

  return (
    <div className="flex gap-x-6 gap-y-4 flex-wrap pl-3 ">
      <div className="w-[6rem]">
        <InputFieldWithTitles
          subtitle="Order ID"
          placeHolder="Add ID"
          isEditable={isEditable}
          changeValueFunc={(value: string | number) =>
            setDirtyFutureOrder({ ...dirtyFutureOrder, orderId: value.toString() })
          }
          value={dirtyFutureOrder.orderId}
        />
      </div>
      <div className="w-24">
        <InputFieldWithTitles
          subtitle="Client Name"
          placeHolder="Add ID"
          isEditable={isEditable}
          changeValueFunc={(value: string | number) =>
            setDirtyFutureOrder({ ...dirtyFutureOrder, clientName: value.toString() })
          }
          value={dirtyFutureOrder.clientName || ''}
        />
      </div>
      <div className="w-36">
        <DateSelect
          selectedDate={(dirtyFutureOrder.futureOrderDate?.toDate() || new Date()).toLocaleDateString('en-UK')}
          setSelectedDate={(value: string) =>
            setDirtyFutureOrder({ ...dirtyFutureOrder, futureOrderDate: Timestamp.fromDate(new Date(value)) })
          }
          subTitle="Order date"
        />
      </div>
      <div className="w-36">
        <OptionSelect
          placeHolder="Product"
          disabled={!isEditable}
          currentOption={products.find((item) => item.uuid == dirtyFutureOrder.productId)?.name || ''}
          options={products.map((item) => item.name) || []}
          setCurrentOption={(productN: string) =>
            setDirtyFutureOrder({
              ...dirtyFutureOrder,
              productId: products.find((item) => item.name == productN)?.uuid || productN,
            })
          }
          subtitle="Product"
        />
      </div>
      <div className="w-44 flex">
        <InputFieldWithTitles
          isEditable={isEditable}
          subtitle="Price per unit"
          placeHolder="Price per unit"
          changeValueFunc={(value: string | number) =>
            setDirtyFutureOrder({
              ...dirtyFutureOrder,
              orderDetails: { ...dirtyFutureOrder.orderDetails, pricePerUnit: Number(value) },
            })
          }
          isNumeric={true}
          value={dirtyFutureOrder.orderDetails.pricePerUnit || ''}
        />
        <OptionSelect
          placeHolder="Currency"
          disabled={!isEditable}
          currentOption={orderDetails.currency || 'USD'}
          options={currencies.map((currency) => ({ value: currency, text: getCurrencySymbol(currency) })) || []}
          setCurrentOption={(currency: string) =>
            setDirtyFutureOrder({ ...dirtyFutureOrder, orderDetails: { ...orderDetails, currency } })
          }
          subtitle="Currency"
        />
      </div>

      {orderDetails.currency && (
        <div>
          <div className="flex-col text-sm text-gray-600">
            <div>USD price</div>
            <div
              className="pt-2 overflow-hidden text-ellipsis whitespace-nowrap max-w-[80px]"
              title={formatToPriceInDollars(
                getCurrencyInUSD(orderDetails.currency || 'USD') * (orderDetails.pricePerUnit || 1),
              )}
            >
              {formatToPriceInDollars(
                getCurrencyInUSD(orderDetails.currency || 'USD') * (orderDetails.pricePerUnit || 1),
              )}
            </div>
          </div>
        </div>
      )}

      <div className="w-24">
        <InputFieldWithTitles
          isEditable={isEditable}
          subtitle="Quantity"
          placeHolder="Quantity"
          changeValueFunc={(value: string | number) =>
            setDirtyFutureOrder({
              ...dirtyFutureOrder,
              orderDetails: { ...dirtyFutureOrder.orderDetails, quantity: Number(value) },
            })
          }
          isNumeric={true}
          value={dirtyFutureOrder.orderDetails.quantity || ''}
        />
      </div>

      <div>
        <div className="h-full flex flex-col justify-between">
          <p className="text-sm text-secondary_text ">Date Range</p>
          <DeliveryDatePicker
            deliveryDates={dirtyFutureOrder.orderDetails.deliveryDates || []}
            quantity={dirtyFutureOrder.orderDetails.quantity}
            isDisabled={!isEditable}
            setDeliveryDates={(value: MonthYear[] | undefined) =>
              value &&
              setDirtyFutureOrder({
                ...dirtyFutureOrder,
                orderDetails: { ...dirtyFutureOrder.orderDetails, deliveryDates: value },
              })
            }
          />
        </div>
      </div>
      <div className="min-w-24">
        <OptionSelect
          disabled={!isEditable}
          placeHolder="Delivery"
          currentOption={dirtyFutureOrder.orderDetails.deliveryType || ''}
          options={futureOrderProductDistributionTypes.map((item) => item.name)}
          setCurrentOption={(value: string) =>
            setDirtyFutureOrder({
              ...dirtyFutureOrder,
              orderDetails: { ...dirtyFutureOrder.orderDetails, deliveryType: value },
            })
          }
          subtitle="Delivery "
        />
      </div>
      <div className="w-[8rem]">
        <InputFieldWithTitles
          isEditable={false}
          subtitle="Total price"
          placeHolder="Total price"
          changeValueFunc={() => {}}
          isNumeric={true}
          value={
            dirtyFutureOrder.orderDetails.pricePerUnit && dirtyFutureOrder.orderDetails.quantity
              ? '$' +
                (dirtyFutureOrder.orderDetails.pricePerUnit * dirtyFutureOrder.orderDetails.quantity).toLocaleString() +
                ' USD'
              : ''
          }
        />
      </div>
      {isEditable && (
        <ButtonDesigned
          style={isFormValid() ? 'primary' : 'outline'}
          className="mt-auto w-32 p-1"
          disableButton={!isFormValid()}
          onButtonClick={handleApplyClick}
        >
          Apply
        </ButtonDesigned>
      )}
    </div>
  );
};
